import { EN_PF_SEARCH_SORT_CODE } from '../enum/EN_PF_SEARCH_SORT_CODE';
import { filter, groupBy, isNil, map, pickBy, sortBy, toPairs } from 'ramda';

import { ISearchListPlaceItemV7 } from '../interfaces/ISearchListPlaceItem';
import { ISearchListQueryParams } from '../interfaces/ISearchListRequestParams';
import { IBuildListParams, TSearchListDefaultParamsType } from '../policies/ISearchListPolicy';
import { IPolicy } from './IPolicy';
import { IGroupSearchList, IHeader, ISearchListPolicy, TGroup } from './ISearchListPolicy';
import { IAdSectionInfo } from '../interfaces/IAdSectionInfo';
import { IGroupManager, createGroupManager } from './groupManager';

const generalHeader = (_: string, index: number): IHeader => ({ index, title: '비노출' });

export default class SearchListRecommendPolicy implements ISearchListPolicy {
  private groupManager: IGroupManager;

  constructor(private policy: IPolicy) {
    this.groupManager = createGroupManager({ generalHeader });
  }

  buildList({ adSections = [], items = [] }: IBuildListParams): IGroupSearchList[] {
    // 숙소 리스트 그룹핑
    const allPlaces = groupBy(this.getGroupByFunction())(items);
    // 숙소 그룹 유효성
    const filterPlaces = filter(this.getValidGroup())(allPlaces);
    const pairedPlaces = toPairs(filterPlaces);
    // 슥소 그룹 정렬 순서
    const sortedPlaces = sortBy(this.getGroupOrderFunction(adSections))(pairedPlaces);
    // 숙소 정보 맵핑
    const mappedPlaces = sortedPlaces.map(this.getGroupMapping(adSections));

    return mappedPlaces;
  }

  getPolicy(): IPolicy {
    return this.policy;
  }

  getDefaultQueryParamsFunction() {
    return (query?: ISearchListQueryParams) => {
      const reservable = Boolean(this.policy.reservable.default);
      const baseQueries = {
        rentType: reservable ? 1 : 0,
        stayType: reservable ? 1 : 0,
        sort: this.policy.subFilter.sort.default[0] || EN_PF_SEARCH_SORT_CODE.YANOLJA_DEFAULT_ORDER,
        themes: (this.policy.subFilter.themes?.default || []).join(','),
      };

      const policyBaseQueries: TSearchListDefaultParamsType = {
        advert: 'AROUND',
        limit: 20,
        page: 1,
        searchType: this.policy.catkey,
        [this.policy.category]: 1,
        ...baseQueries,
        ...this.policy.shortCut,
        ...query,
      };
      return pickBy<TSearchListDefaultParamsType, TSearchListDefaultParamsType>(
        (value) => !isNil(value),
        policyBaseQueries,
      );
    };
  }

  getGroupByFunction() {
    return this.groupManager.createGroupByFunction();
  }

  getValidGroup() {
    return (list: ISearchListPlaceItemV7[]) => {
      return list.length > 0;
    };
  }

  getGroupMapping(adSections: IAdSectionInfo[]) {
    return (group: TGroup, index: number): IGroupSearchList => {
      const groupName: string = group[0] || '';
      const list: ISearchListPlaceItemV7[] = group[1] || [];

      const isSinglePrice = (item: ISearchListPlaceItemV7): boolean => item.displayPrices.length === 1;
      const mappingList = map((item: ISearchListPlaceItemV7) => {
        return Object.assign(item, { isSinglePrice: isSinglePrice(item), isShowLabel: true });
      });

      return {
        header: this.getGroupHeaderFunction(adSections)(groupName, index),
        list: mappingList(list),
        cellType: this.getGroupItemCellTypeFunction(adSections)(group),
      };
    };
  }

  getGroupOrderFunction(adSections: IAdSectionInfo[]) {
    return this.groupManager.createGroupOrderFunction(adSections);
  }

  getGroupHeaderFunction(adSections: IAdSectionInfo[]) {
    return this.groupManager.createGroupHeaderFunction(adSections);
  }

  getGroupItemCellTypeFunction(adSections: IAdSectionInfo[]) {
    return this.groupManager.createGroupItemCellTypeFunction(adSections);
  }
}
